import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject, input, output, signal, ChangeDetectionStrategy } from '@angular/core';
import { GsIconComponent, GsOptimizedImageComponent } from 'libs/shared/src/components';
import { GsDropdownMenuItem } from 'libs/shared/src/controllers/gs-dropdown/models/dropdown-item';
import { GsStructTarget } from 'libs/shared/src/models';
import { ImageService, SystemService } from 'libs/shared/src/services';

/**
 * Mobile sidebar drill-down navigation.
 *
 * Animation is pure CSS: every visited layer renders side-by-side in a flex row
 * that's translated by `-stack().length * 100%`. Popped layers stay in the DOM
 * until `transitionend` so the back-slide remains visible.
 */
@Component({
  selector: 'sidebar-menu',
  imports: [CommonModule, GsOptimizedImageComponent, GsIconComponent],
  templateUrl: './sidebar-menu.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: ``
})
export class SidebarMenuComponent {
  // Services
  protected readonly imageService = inject(ImageService);
  private readonly system = inject(SystemService);

  // Inputs & Models
  /** Top-level menu items rendered when the user is at the root view */
  readonly items = input<GsDropdownMenuItem[]>([]);
  /** Whether the host sidebar is currently open. Closing resets the drill state. */
  readonly sidebarOpen = input<boolean>(false);
  /** Tailwind classes applied to every row (font, spacing, transition) — never colors, those belong in `menuItemColorClasses` */
  readonly menuItemClasses = input<string>('');
  /** Base color classes (text + bg) of a row — replaced by the active classes on the current page */
  readonly menuItemColorClasses = input<string>('');
  /** Tailwind classes applied to a row that matches the current page */
  readonly activeMenuItemClasses = input<string>('');
  /** Tailwind classes applied to a root-level row that matches the current page (text + background) */
  readonly activeRootMenuItemClasses = input<string>('');
  /** Tailwind hover classes (text + bg) applied to root-layer items */
  readonly hoverRootItemClasses = input<string>('');
  /** Tailwind hover classes (text + bg) applied to sub-layer (drilled) items */
  readonly hoverSubItemClasses = input<string>('');
  /** Whether to underline the row matching the current page */
  readonly activeUnderlined = input<boolean>(false);
  /** Tailwind margin between glyph icon and label at the root level */
  readonly glyphMarginClass = input<string>('mr-12');
  /** Tailwind margin between thumbnail and label inside a drilled view */
  readonly photoMarginClass = input<string>('mr-5');
  /** Pre-translated label of the back button shown in drilled views */
  readonly backLabel = input<string>('Back');
  /** ARIA label for the navigation landmark */
  readonly ariaLabel = input<string>('Navigation menu');

  // Outputs
  /** Fires when the user activates a leaf item; the host can close the sidebar in response. */
  readonly itemActivated = output<GsDropdownMenuItem>();

  // Properties & Signals
  /** Drilled-into parents — empty means the user is at the root view. */
  protected readonly stack = signal<GsDropdownMenuItem[]>([]);
  /** May be longer than `stack` while a back-slide is in flight; pruned on `transitionend`. */
  protected readonly renderedStack = signal<GsDropdownMenuItem[]>([]);
  /** Whether the menu is currently inside a drilled sub-level. */
  readonly isDrilled = computed<boolean>(() => this.stack().length > 0);
  /** Container offset: -100% per drilled level. */
  protected readonly translateStyle = computed<string>(() => `translateX(-${this.stack().length * 100}%)`);
  /** Leading null is the root layer; subsequent entries are drilled levels. */
  protected readonly layers = computed<(GsDropdownMenuItem | null)[]>(() => [null, ...this.renderedStack()]);

  constructor() {
    effect(() => {
      if (!this.sidebarOpen()) {
        this.reset();
      }
    });
  }

  // Methods
  /**
   * Drills into the given item. No-op if the item has no children.
   * @param item The menu item the user activated
   */
  drillInto(item: GsDropdownMenuItem): void {
    if (!item.items?.length) return;
    const newStack = [...this.stack(), item];
    this.stack.set(newStack);
    this.renderedStack.update((rendered) => {
      // Reuse rendered layers when they share a prefix with the new stack
      // (e.g. drilling forward while a back-slide is still in flight).
      const matchesPrefix = rendered.length >= newStack.length && newStack.every((s, i) => rendered[i].iD === s.iD);
      return matchesPrefix ? rendered : newStack;
    });
  }

  /** Pops the top of the stack; the popped layer is pruned on `transitionend`. */
  drillBack(): void {
    if (this.stack().length === 0) return;
    this.stack.update((s) => s.slice(0, -1));
  }

  /** Resets the menu to the root view. */
  reset(): void {
    this.stack.set([]);
    this.renderedStack.set([]);
  }

  /**
   * Handles tap / Enter / Space on a row. Drills in if the row has children;
   * otherwise navigates to its link via {@link SystemService.openLink}.
   * @param item The activated item
   * @param event The triggering DOM event
   */
  activate(item: GsDropdownMenuItem, event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    if (item.items?.length) {
      this.drillInto(item);
      return;
    }
    if (item.linkUrl) {
      const isExternal = item.linkUrl.startsWith('http');
      const target = isExternal || item.linkTarget === '_blank' ? GsStructTarget._blank : GsStructTarget._self;
      this.system.openLink(item.linkUrl, target);
      this.itemActivated.emit(item);
    }
  }

  /**
   * Whether the row matches the current page URL (used for the active highlight).
   * Mirrors GsDropdownController.isItemActive.
   * @param item The menu item
   * @returns true when the item's linkUrl matches the current relative URL
   */
  isItemActive(item: GsDropdownMenuItem): boolean {
    return !!item.linkUrl && item.linkUrl === this.system.currentRelativeLink();
  }

  /**
   * Whether the item itself or any of its descendants matches the current page URL.
   * Used to propagate active styling up to parent items in the drill-down menu.
   * @param item The menu item
   * @returns true when the item or any descendant is the current page
   */
  isItemOrDescendantActive(item: GsDropdownMenuItem): boolean {
    if (this.isItemActive(item)) return true;
    return item.items?.some((child) => this.isItemOrDescendantActive(child)) ?? false;
  }

  /**
   * Whether every item of a layer carries a photo. Thumbnails are all-or-nothing per layer,
   * otherwise items without a title image leave a gap next to their label.
   * @param items The items of one menu layer
   * @returns true when the layer is non-empty and each item has a photo
   */
  allItemsHavePhoto(items: GsDropdownMenuItem[]): boolean {
    return items.length > 0 && items.every((item) => !!item.photo);
  }

  /**
   * Prunes popped layers once the back-slide finishes.
   * @param event Native transitionend event from the layers row
   */
  protected onSlideEnd(event: TransitionEvent): void {
    // Ignore bubbled events from descendants and non-transform properties.
    if (event.propertyName !== 'transform' || event.target !== event.currentTarget) return;
    const activeDepth = this.stack().length;
    this.renderedStack.update((rendered) => (rendered.length > activeDepth ? rendered.slice(0, activeDepth) : rendered));
  }

  /** Track-by for the @for over layers — string for the root sentinel, item iD otherwise. */
  protected trackLayer = (_: number, parent: GsDropdownMenuItem | null): string | number => (parent === null ? 'root' : parent.iD);
  /** Track-by for the @for over items. */
  protected trackItem = (_: number, item: GsDropdownMenuItem): number => item.iD;
}
